const navigationControllers = new WeakMap();
const hoverNavigationKey = 'si:mega-hover-navigation-lock';
const blockedPrefetchPath = /(^|\/)(cart|checkout|checkouts|account|search|challenge|password|apps|tools|admin|discount)(\/|$)/;

function initializeNavigation(root) {
  if (navigationControllers.has(root)) return;
  const controller = new AbortController();
  const { signal } = controller;
  const listen = (target, event, callback, options = {}) => target.addEventListener(event, callback, { ...options, signal });
  const desktop = matchMedia('(min-width: 992px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const panelTimers = new Map();
  const panelMotionDuration = 300;
  const panelCloseDelay = 200;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const drawer = root.querySelector('[data-navigation-drawer]');
  const opener = root.querySelector('[data-navigation-open]');
  const closer = root.querySelector('[data-navigation-close]');
  const items = [...root.querySelectorAll('[data-navigation-item]')].map((element) => ({
    element,
    heading: element.querySelector('[data-navigation-heading]'),
    link: element.querySelector('[data-navigation-link]'),
    toggle: element.querySelector('[data-navigation-toggle]'),
    panel: element.querySelector('[data-navigation-panel]'),
  }));
  const prefetched = new WeakSet();
  const rules = new Set();
  const inertElements = new Map();
  let active = null;
  let mobileOpen = false;
  let previousOverflow = '';
  let pointerTrail = [];
  let aim = null;
  let aimTimer = 0;
  let pendingItem = null;
  let hoverReady = false;
  let frame = 0;
  let navigationTimer = 0;
  let incomingNavigation = false;
  try {
    const storedAt = Number(sessionStorage.getItem(hoverNavigationKey));
    incomingNavigation = storedAt > 0 && Date.now() - storedAt < 15000;
    sessionStorage.removeItem(hoverNavigationKey);
  } catch {}

  const controls = (element) => [...element.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]')]
    .filter((control) => control.getClientRects().length && !control.closest('[inert], [hidden]'));
  const fineMouse = (event) => desktop.matches && finePointer.matches && event.pointerType === 'mouse';
  const hasPanelFocus = () => active?.panel.contains(document.activeElement);
  const itemFor = (target) => target instanceof Node ? items.find((item) => item.heading.contains(target)) : null;
  const containsActive = (target) => target instanceof Node && active?.element.contains(target);

  function cancelAim() {
    clearTimeout(aimTimer);
    aimTimer = 0;
    aim = null;
    pendingItem = null;
  }

  function clearPanelTimer(panel) {
    clearTimeout(panelTimers.get(panel));
    panelTimers.delete(panel);
  }

  function finishPanelClose(panel) {
    clearPanelTimer(panel);
    panel.dataset.state = 'closed';
    panel.inert = true;
    panel.hidden = true;
    panel.classList.remove('show');
  }

  function showPanel(panel) {
    clearPanelTimer(panel);
    const wasHidden = panel.hidden;
    panel.hidden = false;
    panel.inert = false;
    panel.classList.add('show');
    if (!desktop.matches || reducedMotion.matches) {
      panel.dataset.state = 'open';
      return;
    }
    if (wasHidden) {
      panel.dataset.state = 'closed';
      panel.getBoundingClientRect();
    }
    panel.dataset.state = 'opening';
    panelTimers.set(panel, setTimeout(() => {
      if (panel.dataset.state === 'opening') panel.dataset.state = 'open';
      panelTimers.delete(panel);
    }, panelMotionDuration));
  }

  function hidePanel(panel, immediate = false) {
    clearPanelTimer(panel);
    panel.inert = true;
    if (immediate || !desktop.matches || reducedMotion.matches) {
      finishPanelClose(panel);
      return;
    }
    const closeHold = panel.dataset.state === 'opening' ? 0 : panelCloseDelay;
    panel.style.setProperty('--navigation-close-hold', `${closeHold}ms`);
    panel.dataset.state = 'closing';
    panelTimers.set(panel, setTimeout(() => finishPanelClose(panel), closeHold + panelMotionDuration));
  }

  function closeMega(restoreFocus = false, immediate = false) {
    cancelAim();
    if (!active) return;
    const item = active;
    active = null;
    if (restoreFocus) item.toggle.focus({ preventScroll: true });
    item.toggle.setAttribute('aria-expanded', 'false');
    hidePanel(item.panel, immediate);
  }

  function prefetchPanel(panel) {
    if (/(^|\/)account(\/|$)/.test(location.pathname) || prefetched.has(panel)) return;
    const connection = navigator.connection;
    if (connection?.saveData || connection?.effectiveType?.includes('2g')) return;
    if (!HTMLScriptElement.supports?.('speculationrules')) return;
    const urls = [...new Set([...panel.querySelectorAll('a[href]:not([target]):not([download])')].flatMap((link) => {
      try {
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.hash || blockedPrefetchPath.test(url.pathname) || url.pathname === location.pathname) return [];
        return [url.href];
      } catch { return []; }
    }))];
    if (!urls.length) return;
    prefetched.add(panel);
    const rule = document.createElement('script');
    rule.type = 'speculationrules';
    rule.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls, eagerness: 'eager' }] });
    document.head.append(rule);
    rules.add(rule);
  }

  function positionPanel() {
    if (active && desktop.matches) root.style.setProperty('--navigation-panel-top', `${Math.max(0, active.panel.getBoundingClientRect().top)}px`);
  }

  function openMega(item, hover = false) {
    if (hover && hasPanelFocus()) return;
    cancelAim();
    if (active === item) return;
    closeMega();
    active = item;
    item.toggle.setAttribute('aria-expanded', 'true');
    showPanel(item.panel);
    positionPanel();
    if (desktop.matches) prefetchPanel(item.panel);
  }

  function setMobileOpen(open, restoreFocus = true) {
    if (open === mobileOpen) return;
    mobileOpen = open;
    closeMega();
    opener.setAttribute('aria-expanded', String(open));
    if (open) {
      drawer.inert = false;
      drawer.classList.add('show');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-label', 'Main menu');
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      let branch = drawer;
      while (branch.parentElement && branch !== document.body) {
        for (const sibling of branch.parentElement.children) {
          if (sibling === branch || !(sibling instanceof HTMLElement) || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue;
          inertElements.set(sibling, sibling.inert);
          sibling.inert = true;
        }
        branch = branch.parentElement;
      }
      closer.focus({ preventScroll: true });
    } else {
      for (const [element, wasInert] of inertElements) element.inert = wasInert;
      inertElements.clear();
      document.body.style.overflow = previousOverflow;
      drawer.classList.remove('show');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      drawer.removeAttribute('aria-label');
      if (restoreFocus) opener.focus({ preventScroll: true });
      drawer.inert = !desktop.matches;
    }
  }

  function withinTriangle(point, triangle) {
    const area = (a, b, c) => (a.x - c.x) * (b.y - c.y) - (b.x - c.x) * (a.y - c.y);
    const values = [area(point, triangle.apex, triangle.left), area(point, triangle.left, triangle.right), area(point, triangle.right, triangle.apex)];
    return !(values.some((value) => value < 0) && values.some((value) => value > 0));
  }

  function beginAim(event) {
    if (!active) return false;
    const point = { x: event.clientX, y: event.clientY };
    const previous = [...pointerTrail].reverse().find((entry) => Math.hypot(entry.x - point.x, entry.y - point.y) >= 4);
    if (!previous) return false;
    const dx = point.x - previous.x;
    const dy = point.y - previous.y;
    const bounds = active.panel.getBoundingClientRect();
    const y = bounds.top + 12;
    if (dy <= 0 || y <= point.y) return false;
    const x = point.x + dx / dy * (y - point.y);
    if (x < bounds.left || x > bounds.right) return false;
    cancelAim();
    const width = Math.min(144, Math.max(48, Math.abs(dx) + 48));
    aim = { apex: point, left: { x: Math.max(bounds.left, x - width), y }, right: { x: Math.min(bounds.right, x + width), y } };
    aimTimer = setTimeout(() => {
      const next = pendingItem;
      cancelAim();
      if (next?.heading.matches(':hover')) openMega(next, true);
      else if (!active?.element.matches(':hover') && !hasPanelFocus()) closeMega();
    }, 300);
    return true;
  }

  items.forEach((item) => {
    finishPanelClose(item.panel);
    listen(item.element, 'pointerenter', (event) => {
      if (!fineMouse(event) || !hoverReady || hasPanelFocus()) return;
      if (aim && active !== item && withinTriangle({ x: event.clientX, y: event.clientY }, aim)) {
        pendingItem = item;
        return;
      }
      openMega(item, true);
    });
    listen(item.element, 'pointerleave', (event) => {
      if (!fineMouse(event)) return;
      if (!hoverReady && !itemFor(event.relatedTarget)) hoverReady = true;
      if (active !== item || item.panel.contains(event.relatedTarget)) return;
      if (beginAim(event)) return;
      const next = itemFor(event.relatedTarget);
      if (next) openMega(next, true);
      else if (!hasPanelFocus()) closeMega();
    });
    listen(item.panel, 'pointerenter', (event) => { if (fineMouse(event)) cancelAim(); });
    listen(item.panel, 'pointerleave', (event) => {
      if (!fineMouse(event) || item.heading.contains(event.relatedTarget) || itemFor(event.relatedTarget)) return;
      if (!hasPanelFocus()) closeMega();
    });
    listen(item.toggle, 'click', (event) => {
      if (active === item) closeMega();
      else {
        openMega(item);
        if (event.detail === 0 && desktop.matches) controls(item.panel)[0]?.focus();
      }
    });
    listen(item.toggle, 'keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      openMega(item);
      const links = controls(item.panel);
      (event.key === 'ArrowUp' ? links.at(-1) : links[0])?.focus();
    });
    listen(item.link, 'click', (event) => {
      if (desktop.matches && event.pointerType === 'touch') {
        event.preventDefault();
        if (active === item) closeMega(); else openMega(item);
        return;
      }
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      try {
        if (new URL(item.link.href).origin !== location.origin) return;
        const storedAt = String(Date.now());
        sessionStorage.setItem(hoverNavigationKey, storedAt);
        navigationTimer = setTimeout(() => {
          if (sessionStorage.getItem(hoverNavigationKey) === storedAt) sessionStorage.removeItem(hoverNavigationKey);
        }, 15000);
      } catch {}
    });
    listen(item.panel, 'keydown', (event) => {
      if (event.key !== 'Tab' || !desktop.matches) return;
      const links = controls(item.panel);
      if (event.shiftKey && document.activeElement === links[0]) {
        event.preventDefault();
        item.toggle.focus();
      }
    });
  });

  listen(root, 'pointermove', (event) => {
    if (!fineMouse(event) || !hoverReady) return;
    const now = performance.now();
    const point = { x: event.clientX, y: event.clientY, time: now };
    pointerTrail = [...pointerTrail.filter((entry) => now - entry.time <= 160), point].slice(-6);
    if (!aim) return;
    if (active?.panel.contains(event.target)) { cancelAim(); return; }
    if (withinTriangle(point, aim)) return;
    cancelAim();
    const next = itemFor(event.target);
    if (next) openMega(next, true);
    else if (!containsActive(event.target) && !hasPanelFocus()) closeMega();
  }, { passive: true });
  listen(root, 'pointerleave', (event) => {
    if (!fineMouse(event)) return;
    hoverReady = true;
    if (!hasPanelFocus()) closeMega();
  });
  listen(document, 'focusin', (event) => {
    if (desktop.matches && active && !containsActive(event.target)) closeMega();
    if (mobileOpen && !drawer.contains(event.target)) closer.focus();
  });
  listen(document, 'pointerdown', (event) => {
    if (desktop.matches && active && !containsActive(event.target)) closeMega();
    if (mobileOpen && !drawer.contains(event.target) && !opener.contains(event.target)) setMobileOpen(false);
  });
  listen(document, 'keydown', (event) => {
    if (event.key === 'Escape') {
      if (active) { event.preventDefault(); closeMega(true); }
      else if (mobileOpen) { event.preventDefault(); setMobileOpen(false); }
    }
    if (event.key !== 'Tab' || !mobileOpen) return;
    const links = controls(drawer);
    if (event.shiftKey && document.activeElement === links[0]) {
      event.preventDefault(); links.at(-1)?.focus();
    } else if (!event.shiftKey && document.activeElement === links.at(-1)) {
      event.preventDefault(); links[0]?.focus();
    }
  });
  listen(opener, 'click', () => setMobileOpen(!mobileOpen));
  listen(closer, 'click', () => setMobileOpen(false));
  listen(desktop, 'change', () => {
    const focusWasInside = drawer.contains(document.activeElement);
    const focusWasOnOpener = document.activeElement === opener;
    setMobileOpen(false, false);
    closeMega(false, true);
    items.forEach((item) => finishPanelClose(item.panel));
    drawer.inert = !desktop.matches;
    if (focusWasInside || focusWasOnOpener) (desktop.matches ? items[0]?.link : opener)?.focus({ preventScroll: true });
  });
  listen(window, 'scroll', positionPanel, { passive: true });
  listen(window, 'resize', positionPanel, { passive: true });
  listen(window, 'pagehide', () => {
    closeMega(false, true);
    setMobileOpen(false, false);
    items.forEach((item) => finishPanelClose(item.panel));
  });
  listen(reducedMotion, 'change', () => {
    if (!reducedMotion.matches) return;
    items.forEach((item) => {
      if (item === active) {
        clearPanelTimer(item.panel);
        item.panel.dataset.state = 'open';
      } else finishPanelClose(item.panel);
    });
  });
  listen(window, 'pageshow', (event) => {
    if (!event.persisted) return;
    hoverReady = !items.some((item) => item.heading.matches(':hover'));
  });
  drawer.inert = !desktop.matches;
  frame = requestAnimationFrame(() => {
    hoverReady = !incomingNavigation && !items.some((item) => item.heading.matches(':hover'));
  });
  navigationControllers.set(root, () => {
    setMobileOpen(false, false);
    closeMega();
    items.forEach((item) => finishPanelClose(item.panel));
    controller.abort();
    cancelAnimationFrame(frame);
    clearTimeout(navigationTimer);
    rules.forEach((rule) => rule.remove());
    root.style.removeProperty('--navigation-panel-top');
    navigationControllers.delete(root);
  });
}

const initializeWithin = (scope) => scope.querySelectorAll('[data-si-navigation]').forEach(initializeNavigation);
initializeWithin(document);
document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-si-navigation]').forEach((root) => navigationControllers.get(root)?.());
});
